/**
 * Cart duck: global Redux slice that stores the customer's shopping cart.
 *
 * Cart items are kept minimal (listing id, author id, quantity, delivery method) and persisted to
 * localStorage so the cart survives page reloads for both guests and logged-in users. Full listing
 * and author entities are re-fetched on the Cart page via its loadData function.
 */
import { createSlice } from '@reduxjs/toolkit';

const CART_STORAGE_KEY = 'cart';

// A cart line is identified by listing + delivery method + pricing option, so the same listing
// added with a different delivery method or pricing option becomes a separate line.
const cartLineKey = (listingId, deliveryMethod, pricingOptionId) =>
  `${listingId}::${deliveryMethod || 'none'}::${pricingOptionId || 'none'}`;

// Ensure every item has a stable `key` and re-key the selection map. Supports carts persisted
// before line keys existed (falls back to the item's listingId selection, defaulting to selected).
const withLineKeys = (items, selected) => {
  const nextItems = items.map(item => ({
    ...item,
    key: item.key || cartLineKey(item.listingId, item.deliveryMethod, item.pricingOptionId),
  }));
  const nextSelected = {};
  nextItems.forEach(item => {
    const prev = selected[item.key] !== undefined ? selected[item.key] : selected[item.listingId];
    nextSelected[item.key] = prev !== undefined ? prev : true;
  });
  return { items: nextItems, selected: nextSelected };
};

// Only one seller can be selected for checkout at a time. Deselect every item that belongs to a
// different seller than the one just selected.
const deselectOtherSellers = (state, authorId) => {
  state.items.forEach(item => {
    if (item.authorId !== authorId) {
      state.selected[item.key] = false;
    }
  });
};

// ================ localStorage helpers ================ //

const hasStorage = () => typeof window !== 'undefined' && !!window.localStorage;

/**
 * Read the persisted cart from localStorage.
 * @returns {{items: Array, selected: Object}|null}
 */
export const loadCartState = () => {
  if (!hasStorage()) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    const items = Array.isArray(parsed?.items) ? parsed.items : [];
    const selected = parsed?.selected && typeof parsed.selected === 'object' ? parsed.selected : {};
    return { items, selected };
  } catch (e) {
    return null;
  }
};

const saveCartState = state => {
  if (!hasStorage()) {
    return;
  }
  try {
    const { items, selected } = state;
    window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify({ items, selected }));
  } catch (e) {
    // Ignore write errors (e.g. storage full or disabled).
  }
};

// ================ Slice ================ //

const initialState = {
  items: [],
  selected: {},
  hydrated: false,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    hydrateCart: (state, action) => {
      const { items, selected } = action.payload || {};
      const safeItems = Array.isArray(items) ? items : [];
      const safeSelected = selected && typeof selected === 'object' ? selected : {};
      const keyed = withLineKeys(safeItems, safeSelected);
      state.items = keyed.items;
      state.selected = keyed.selected;
      state.hydrated = true;
    },
    addItem: (state, action) => {
      const { listingId, authorId, quantity = 1, deliveryMethod, pricingOptionId, maxStock } =
        action.payload || {};
      const key = cartLineKey(listingId, deliveryMethod, pricingOptionId);
      const cap = typeof maxStock === 'number' ? maxStock : Infinity;
      const existing = state.items.find(item => item.key === key);
      if (existing) {
        existing.quantity = Math.min(existing.quantity + quantity, cap);
      } else {
        state.items.push({
          key,
          listingId,
          authorId,
          quantity: Math.min(quantity, cap),
          deliveryMethod,
          pricingOptionId,
        });
      }
      state.selected[key] = true;
      deselectOtherSellers(state, authorId);
    },
    removeItem: (state, action) => {
      const key = action.payload;
      state.items = state.items.filter(item => item.key !== key);
      delete state.selected[key];
    },
    setQuantity: (state, action) => {
      const { key, quantity } = action.payload || {};
      const existing = state.items.find(item => item.key === key);
      if (existing && quantity > 0) {
        existing.quantity = quantity;
      }
    },
    setItemSelected: (state, action) => {
      const { key, selected } = action.payload || {};
      state.selected[key] = !!selected;
      if (selected) {
        const item = state.items.find(i => i.key === key);
        if (item) {
          deselectOtherSellers(state, item.authorId);
        }
      }
    },
    setSellerSelected: (state, action) => {
      const { authorId, selected } = action.payload || {};
      state.items
        .filter(item => item.authorId === authorId)
        .forEach(item => {
          state.selected[item.key] = !!selected;
        });
      if (selected) {
        deselectOtherSellers(state, authorId);
      }
    },
    clearSeller: (state, action) => {
      const authorId = action.payload;
      state.items = state.items.filter(item => {
        const remove = item.authorId === authorId;
        if (remove) {
          delete state.selected[item.key];
        }
        return !remove;
      });
    },
    clearCart: state => {
      state.items = [];
      state.selected = {};
    },
  },
});

const {
  hydrateCart,
  addItem,
  removeItem,
  setQuantity,
  setItemSelected,
  setSellerSelected,
  clearSeller,
  clearCart,
} = cartSlice.actions;

export default cartSlice.reducer;

// ================ Thunks (dispatch + persist) ================ //

// Persist the cart to localStorage after any mutation.
const persist = getState => saveCartState(getState().cart);

export const hydrateCartFromStorage = () => dispatch => {
  // Always mark the cart hydrated (even when nothing is stored) so the Cart page can stop showing
  // its loading state and render the empty state or items.
  const stored = loadCartState() || { items: [], selected: {} };
  dispatch(hydrateCart(stored));
};

export const addToCart = item => (dispatch, getState) => {
  dispatch(addItem(item));
  persist(getState);
};

export const removeFromCart = key => (dispatch, getState) => {
  dispatch(removeItem(key));
  persist(getState);
};

export const updateCartQuantity = (key, quantity) => (dispatch, getState) => {
  dispatch(setQuantity({ key, quantity }));
  persist(getState);
};

export const selectCartItem = (key, selected) => (dispatch, getState) => {
  dispatch(setItemSelected({ key, selected }));
  persist(getState);
};

export const selectCartSeller = (authorId, selected) => (dispatch, getState) => {
  dispatch(setSellerSelected({ authorId, selected }));
  persist(getState);
};

export const clearCartSeller = authorId => (dispatch, getState) => {
  dispatch(clearSeller(authorId));
  persist(getState);
};

export const emptyCart = () => (dispatch, getState) => {
  dispatch(clearCart());
  persist(getState);
};

// ================ Selectors ================ //

export const selectCartItems = state => state.cart.items;

export const selectCartItemCount = state =>
  state.cart.items.reduce((sum, item) => sum + item.quantity, 0);

export const selectCartSelection = state => state.cart.selected;

export const selectCartHydrated = state => state.cart.hydrated;